/**
 * Camera media auth: the GCS signed query that authorises cloud recording reads.
 *
 * @remarks
 * `/auth/gstorage` mints a query string (not a bearer token) whose `URLPrefix` is
 * scoped per home per account, so a query minted for one home authorises nothing
 * in another. Session changes call {@link invalidateSignedQuery} to drop it at
 * once, and each query accepted in this module carries its in-memory home scope.
 * Persisted values from an earlier session are treated as stale and re-minted.
 */

import { get } from 'svelte/store';

import type { GStorageSignedUrlResponse } from 'src/components/Camera/types/cloud-recordings';
import { currentUnitId } from 'src/stores/account/home';
import { camera_cloud_auth } from 'src/stores/features/cameras/cloud-auth';
import { cameraCloudApi } from '../http';

/** Refresh this long before expiry, so a request never races the deadline. */
const EXPIRY_BUFFER_MS = 5 * 60 * 1000;

/** Statuses a re-signed request may still succeed on. */
const RETRYABLE_STATUSES = new Set([401, 403, 404]);

/** Bumped by every invalidation, so a refresh started before it is discarded. */
let generation = 0;

/** In-flight refresh, shared by concurrent callers. */
let pendingRefresh: Promise<string | null> | null = null;

/** Scope for the in-memory query; persisted legacy queries are re-minted. */
let signedQueryScope: string | null = null;

/**
 * The home a query has to have been minted for to be usable.
 *
 * @remarks
 * A unit id identifies the account as well as the home, so this covers profile
 * switches too.
 */
const activeScope = (): string => get(currentUnitId) ?? '';

/**
 * The stored query, ignoring values not minted for the active home in this
 * module instance.
 */
const storedQuery = (): string | null => {
	const stored = get(camera_cloud_auth);
	return signedQueryScope === activeScope() ? (stored?.token ?? null) : null;
};

const isExpiring = (): boolean => {
	const expiresAt = get(camera_cloud_auth)?.expiresAt;
	if (!storedQuery() || !expiresAt) return true;

	const deadline = new Date(expiresAt).getTime();
	if (Number.isNaN(deadline)) return true;
	return Date.now() >= deadline - EXPIRY_BUFFER_MS;
};

/** Mints a query for the active session; scoped by the JWT on `cameraCloudApi`. */
const requestSignedQuery = async (): Promise<GStorageSignedUrlResponse> =>
	(await cameraCloudApi.get('/auth/gstorage')) as GStorageSignedUrlResponse;

/**
 * Fetches a signed query and stores it.
 *
 * @returns The new query, or `null` when the request failed or was invalidated
 * while in flight.
 */
const mintSignedQuery = async (): Promise<string | null> => {
	const startedAt = generation;
	// Stamped with the home the request was issued under, not the one active when
	// it lands, so a query minted across a switch is stored as already stale.
	const scope = activeScope();
	try {
		const response = await requestSignedQuery();
		if (startedAt !== generation) return null;

		if (!response?.token) throw new Error('response carried no token');
		signedQueryScope = scope;
		camera_cloud_auth.setFromResponse(response);
		return response.token;
	} catch (error) {
		console.error('[media-auth] Failed to mint signed query', error);
		if (startedAt === generation) {
			signedQueryScope = null;
			camera_cloud_auth.clear();
		}
		return null;
	}
};

/**
 * Runs a refresh, or joins the one already in flight.
 *
 * @returns The query afterwards, reread rather than taken from the request, so a
 * refresh issued for the home the session has since left is not handed on.
 */
const refreshAndRead = async (): Promise<string | null> => {
	if (!pendingRefresh) {
		const refresh = mintSignedQuery().finally(() => {
			// A refresh superseded by an invalidation must not clear its replacement.
			if (pendingRefresh === refresh) pendingRefresh = null;
		});
		pendingRefresh = refresh;
	}

	await pendingRefresh;
	return storedQuery();
};

/** The cached signed query, refreshed when missing or close to expiry. */
export const getSignedQuery = async (): Promise<string | null> => {
	if (!isExpiring()) return storedQuery();
	return refreshAndRead();
};

/**
 * Re-mints the signed query after a signed request was rejected.
 *
 * @param rejectedTarget - The URL (or bare query) the rejected request carried.
 * When it no longer holds the stored query another caller has already re-minted,
 * and that result is returned instead of issuing a second request.
 * @returns The current query, or `null` when minting failed.
 */
export const refreshSignedQuery = async (rejectedTarget?: string | null): Promise<string | null> => {
	const current = storedQuery();
	if (current && rejectedTarget && !rejectedTarget.includes(current)) return current;
	return refreshAndRead();
};

/**
 * Creates a one-shot re-sign for a consumer that cannot use {@link fetchSignedMedia}
 * — an `<img>`, a `<video>` or `hls.js`, none of which expose the response.
 *
 * @remarks
 * The attempt is keyed on the **unsigned** source, because re-minting rebuilds the
 * signed URL: a guard keyed on that would re-arm itself and loop on a rejection no
 * query can fix, such as a genuinely missing object. Only the last source is
 * remembered, so returning to an earlier one grants it another attempt.
 *
 * @returns A resign function taking the unsigned source and the URL (or bare query)
 * that was rejected. It resolves to the query to rebuild with, or `null` when this
 * source was already retried, minting failed, or the query is unchanged.
 */
export const createResignGuard = () => {
	let attemptedFor: string | null = null;

	return async (source?: string | null, rejectedTarget?: string | null): Promise<string | null> => {
		if (!source || attemptedFor === source) return null;
		attemptedFor = source;

		const fresh = await refreshSignedQuery(rejectedTarget);
		if (!fresh) return null;
		return rejectedTarget?.includes(fresh) ? null : fresh;
	};
};

/** Drops the signed query and discards any refresh already in flight. */
export const invalidateSignedQuery = (): void => {
	generation += 1;
	pendingRefresh = null;
	signedQueryScope = null;
	camera_cloud_auth.clear();
};

/** Subscribe to signed-query changes, for consumers that must re-resolve on refresh. */
export const watchSignedQuery = camera_cloud_auth.subscribe;

/**
 * Appends a signed query to a media URL.
 *
 * @param baseUrl - The unsigned cloud media URL.
 * @param signedQuery - The query to append.
 * @returns The signed URL, `baseUrl` when it is already signed, or `''` when
 * either argument is missing or `baseUrl` is unparseable — never an unsigned URL.
 */
export const appendSignedQuery = (baseUrl?: string | null, signedQuery?: string | null): string => {
	if (!baseUrl || !signedQuery) return '';
	try {
		if (new URL(baseUrl).searchParams.has('Signature')) return baseUrl;
	} catch {
		return '';
	}
	return `${baseUrl}${baseUrl.includes('?') ? '&' : '?'}${signedQuery}`;
};

/**
 * Signs a media URL for the active session.
 *
 * @param baseUrl - The unsigned cloud media URL.
 * @returns The signed URL, or `''` when no query could be obtained.
 */
export const getSignedMediaUrl = async (baseUrl?: string | null): Promise<string> => {
	if (!baseUrl) return '';
	return appendSignedQuery(baseUrl, await getSignedQuery());
};

/**
 * Fetches signed media, re-signing once when the response suggests the query is
 * no longer valid.
 *
 * @remarks
 * A rejected fetch is retried too, not just a retryable status: GCS omits CORS
 * headers on its 403, so cross-origin a signing failure arrives with no readable
 * status at all. The retry is skipped when re-minting returned the same query,
 * which is what keeps a genuinely missing object from looping.
 *
 * @param baseUrl - The unsigned cloud media URL.
 * @returns The response, or `null` when the URL could not be signed.
 */
export const fetchSignedMedia = async (baseUrl?: string | null): Promise<Response | null> => {
	if (!baseUrl) return null;

	const signedQuery = await getSignedQuery();
	const url = appendSignedQuery(baseUrl, signedQuery);
	if (!url) return null;

	const response = await fetch(url).catch(() => null);
	if (response?.ok) return response;
	if (response && !RETRYABLE_STATUSES.has(response.status)) return response;

	const freshQuery = await refreshSignedQuery(url);
	if (!freshQuery || freshQuery === signedQuery) return response;
	return (await fetch(appendSignedQuery(baseUrl, freshQuery)).catch(() => null)) ?? response;
};
